import { useContext, useEffect, useRef } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { isStripeRedirectResponse } from '@/types/guards';
import { ProviderEnum } from '@/types/common';
import { stripeService } from '@/services/stripe';
import type { StripePaymentResponse } from '@/types/payment';
import { logError } from '@/utils/logger';
import { resolveStripeFor3DS } from '@/utils/stripe3DS';

/**
 * Stripe `handleNextAction` driver for CARD + WALLET 3DS only:
 *   - Cards: `next_action.type === 'redirect_to_url'` → 3DS popup.
 *   - GPay / ApplePay: `next_action.type === 'use_stripe_sdk'` → inline
 *     3DS modal mounted by Stripe.js into the document body.
 *
 * Generic in-page APMs (e.g. Bizum) are handled by their own dedicated driver
 * (<StripeApmNextActionForm/>, wired through the `stripeApmAction` channel), so
 * this component and the whole `threeDSSecurityState` path stay scoped to the
 * card/wallet 3DS concern.
 *
 * The publishable key matters: for publishers that issue different sub-MIDs per
 * payment method the PaymentIntent is created against a specific Stripe account,
 * and calling `handleNextAction` on the wrong instance returns "PaymentIntent
 * not found". We pick the instance by method:
 *   - Wallets (GPay/ApplePay): the Express account → `getStripeExpress()`.
 *   - Cards: `loadStripeResilient` for the card method's publishable key, then
 *     context `stripe`. The stock `loadStripe` memo stays pending forever after
 *     a hung script, even once the card form has recovered, so `handleNextAction`
 *     must use the same loader. Saved cards never mount the card form, so the
 *     shared slot can still hold another method's instance.
 */
export const StripeCard3DSForm = () => {
  const {
    threeDSSecurityState: { params, paymentMethod, handlePaymentSuccess, handlePaymentError },
    stripe,
    paymentMethodSelected,
    paymentMethodsSettings,
    paymentKeys,
  } = useContext(CheckoutContext);
  const isProcessing = useRef(false);

  useEffect(() => {
    if (!params || !isStripeRedirectResponse(params, ProviderEnum.STRIPE)) {
      return;
    }

    if (isProcessing.current) {
      return;
    }

    let cancelled = false;

    const run = async () => {
      // `paymentMethod` from threeDSSecurityState is authoritative because
      // express GPay/ApplePay don't update context's `paymentMethodSelected`.
      // Fall back to context for the regular (non-express) card flow.
      const activeMethod = paymentMethod ?? paymentMethodSelected;

      const stripeForNextAction = await resolveStripeFor3DS(activeMethod, stripe, paymentMethodsSettings, paymentKeys);
      if (cancelled || !stripeForNextAction || isProcessing.current) {
        return;
      }

      isProcessing.current = true;

      stripeForNextAction
        .handleNextAction({
          clientSecret: (params as StripePaymentResponse).stripe.clientSecret,
        })
        .then((result) => {
          if (result.error) {
            // Mirror the redirect-manager mapping: prefer the issuer
            // `decline_code`, fall back to the Stripe error `type`.
            const appChargeErrorType = stripeService.mapStripeErrorCode(result.error.decline_code ?? result.error.type);
            handlePaymentError?.(appChargeErrorType);
            return;
          }

          // `handleNextAction` resolves WITHOUT a top-level `error` even when the
          // 3DS challenge itself succeeds but the issuer then declines the charge
          // (e.g. the "authenticate then fail" test cards). In that case the
          // PaymentIntent lands in a non-authorized terminal status with the
          // decline detail on `last_payment_error`. Treat anything that isn't an
          // authorized status as a failure so the decline is surfaced and the
          // failure analytics fire — otherwise we'd wrongly route to success.
          const paymentIntent = result.paymentIntent;
          const isAuthorized =
            paymentIntent?.status === 'succeeded' ||
            paymentIntent?.status === 'requires_capture' ||
            paymentIntent?.status === 'processing';
          if (!isAuthorized) {
            const lastPaymentError = paymentIntent?.last_payment_error;
            const appChargeErrorType = stripeService.mapStripeErrorCode(
              lastPaymentError?.decline_code ?? lastPaymentError?.code ?? lastPaymentError?.type
            );
            handlePaymentError?.(appChargeErrorType);
            return;
          }

          handlePaymentSuccess?.();
        })
        .catch((error) => {
          logError('Stripe handleNextAction error', error);
          handlePaymentError?.();
        })
        .finally(() => {
          isProcessing.current = false;
        });
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [
    params,
    handlePaymentSuccess,
    handlePaymentError,
    stripe,
    paymentMethod,
    paymentMethodSelected,
    paymentMethodsSettings,
    paymentKeys,
  ]);
  return null;
};
